// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { parseLocalDate } from '@/utils/formatters';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  MessageSquare,
  User,
  Send,
  Loader2,
  Inbox,
  Reply
} from 'lucide-react';
import { toast } from 'sonner';

interface GraphicsMessage {
  id: string;
  order_id: string | null;
  sender_id: string | null;
  sender_type: string;
  content: string;
  attachments: string[] | null;
  is_read: boolean | null;
  created_at: string;
  updated_at: string;
  order?: {
    vendor_id: string;
  };
  sender?: {
    name: string;
    email: string;
  };
}

const VendorMessages: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<GraphicsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    replied: 0,
  });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);

      // First get the vendor profile
      const { data: vendorProfile, error: vendorError } = await supabase
        .from('graphics_vendor_profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (vendorError) throw vendorError;

      // Fetch messages for this vendor through orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('graphics_orders')
        .select('id')
        .eq('vendor_id', vendorProfile.id);

      if (ordersError) throw ordersError;

      const orderIds = ordersData?.map(order => order.id) || [];

      if (orderIds.length === 0) {
        setMessages([]);
        setStats({ total: 0, unread: 0, replied: 0 });
        return;
      }

      // Get messages for these orders
      const { data: messagesData, error: messagesError } = await supabase
        .from('graphics_vendor_messages')
        .select(`
          *,
          sender:profiles!sender_id(name, email)
        `)
        .in('order_id', orderIds)
        .order('created_at', { ascending: false });

      if (messagesError) throw messagesError;

      setMessages(messagesData || []);

      // Calculate stats from real data
      const total = messagesData?.length || 0;
      const unread = messagesData?.filter(msg => !msg.is_read).length || 0;
      const replied = 0; // This would need additional logic based on your requirements

      setStats({
        total,
        unread,
        replied,
      });

    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('graphics_vendor_messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) throw error;

      // Update local state
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      ));
      
      // Recalculate stats
      const updatedMessages = messages.map(msg => 
        msg.id === messageId ? { ...msg, is_read: true } : msg
      );
      const unread = updatedMessages.filter(msg => !msg.is_read).length;
      setStats(prev => ({ ...prev, unread }));
      
      toast.success('Message marked as read');
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Failed to update message status');
    }
  };

  const markAsReplied = async (messageId: string) => {
    // This functionality would need to be implemented based on your requirements
    // For now, we'll just mark it as read
    await markAsRead(messageId);
  };

  const getStatusBadge = (message: GraphicsMessage) => {
    const isRead = message.is_read;
    const statusConfig = {
      unread: { variant: 'destructive' as const, icon: MessageSquare, label: 'Unread' },
      read: { variant: 'secondary' as const, icon: Inbox, label: 'Read' },
    };

    const config = isRead ? statusConfig.read : statusConfig.unread;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return parseLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin mr-2" />
        <span>Loading messages...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Messages</h1>
        <p className="text-muted-foreground">
          Manage your customer communications and inquiries.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All time messages
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unread</CardTitle>
            <Inbox className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.unread}</div>
            <p className="text-xs text-muted-foreground">
              Require attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Replied</CardTitle>
            <Send className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.replied}</div>
            <p className="text-xs text-muted-foreground">
              Successfully responded
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Messages Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Messages</CardTitle>
          <CardDescription>
            Customer inquiries and communications about your graphics services.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {messages.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>From</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {messages.map((message) => (
                  <TableRow key={message.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{message.sender?.name || 'Unknown Sender'}</div>
                          <div className="text-sm text-muted-foreground">
                            {message.sender?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">Message from {message.sender_type}</div>
                        <div className="text-sm text-muted-foreground">
                          {message.content.substring(0, 100)}
                          {message.content.length > 100 && '...'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(message)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(message.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {!message.is_read && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsRead(message.id)}
                          >
                            Mark Read
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Messages Yet</h3>
              <p className="text-muted-foreground mb-4">
                When customers send you messages, they'll appear here.
              </p>
              <p className="text-sm text-muted-foreground">
                Make sure your contact information is visible on your vendor profile.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorMessages;